import React, { FC, useCallback, useState } from 'react';
import styled from 'styles/styled-components';

import media from 'utils/mediaStyle';

import { Button, Card, Skeleton } from 'antd';
import ModalNote from 'components/Checkout/ModalNote';
import MarketItem from 'components/MarketItem/MarketItem';
import { Viewport } from 'components/ViewportProvider';
import { push } from 'connected-react-router';
import { IHomeFavoritePayload, IHomeProductNotePayload } from 'containers/HomePage/types';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { compose, Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';
import { ApplicationRootState } from 'types';
import { Product, TrackingAddToCartAddFromType, TrackingProductImpressionType } from 'types/schema';
import { EventName, sendGA4Event } from 'utils/triggerGA4Event';
import { selectAddedProducts, selectAddingProducts, selectUserPermissions } from '../../../MainLayout/selectors';
import { AddedProduct, AddingProduct } from '../../../MainLayout/types';
import { setProductSourceFavorite, setProductSourceNote } from '../actions';
import {
  HERBS,
  HERBS_MOBILE,
  LETTUCE,
  LETTUCE_MOBILE,
  NAM_TUOI_NGON,
  NAM_TUOI_NGON_MOBILE,
  RAU_CU_TUOI_SACH,
  RAU_CU_TUOI_SACH_MOBILE,
} from '../Images';
import messages from '../messages';
import selectFarmToTablePage, { selectNoteItemLoading } from '../selectors';
import { ContainerState } from '../types';
import { Container, SectionContainer, SectionTitle, SectionTitleBox } from './Partners';

const ProductSourceContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  ${media.lg`
    gap: 24px;
  `};
  &:not(:last-child) {
    margin-bottom: 24px;
    ${media.lg`
      margin-bottom: 40px;
    `};
  }
`;

const List = styled('div')`
  padding: 1px;
  display: flex;
  overflow: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
  gap: 20px;
  ${media.lg`
    display: grid;
    gap: 24px;
    grid-template-columns: repeat(5, minmax(0, 1fr));
  `};
`;

const SkeletonImage = styled(Skeleton.Image)`
  width: 100% !important;
  & > div {
    width: 100% !important;
    height: 200px !important;
  }
`;

const Item = styled('div')`
  ${media.md`
    width:100%;
  `};
  flex: 0 0 180px;
  width: 180px;
`;

const SectionTitleWrapper = styled.div`
  position: relative;
`;

const SectionTitleBackground = styled.img`
  width: 100%;
`;

const SectionTitleText = styled.div`
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: #242527;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  ${media.lg`
    font-size: 24px;
    line-height: 32px;
    `}
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
`;

const ButtonContainer = styled(Button)`
  height: 40px;
  padding: 12px;
  border-radius: 8px;
  color: #43a047;
  border-color: #43a047;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  background: transparent;
  display: flex;
  align-items: center;
  &:hover {
    background: #f0f9f1 !important;
  }
  &:active {
    border-color: #3c9041 !important;
    color: #3c9041 !important;
  }
  ${media.md`
    font-size: 16px;
    `}
`;

interface OwnProps {}

export interface IStateProps {
  farmToTablePage: ContainerState;
  addedProducts: AddedProduct;
  addingProducts: AddingProduct;
  permissions: string[];
  noteItemLoading: boolean;
}

interface IDispatchProps {
  onToggleFavorite: (data: IHomeFavoritePayload) => void;
  onSetProductNote: (data: IHomeProductNotePayload) => void;
  gotoSignIn: () => void;
  gotoTagPage: (path: string) => void;
}

type IProps = OwnProps & IStateProps & IDispatchProps;

const sectionImages = {
  VEGETABLE_FARM: RAU_CU_TUOI_SACH,
  LETTUCE_FARM: LETTUCE,
  HERBS_FARM: HERBS,
  MUSHROOM_FARM: NAM_TUOI_NGON,
};

const sectionImagesMobile = {
  VEGETABLE_FARM: RAU_CU_TUOI_SACH_MOBILE,
  LETTUCE_FARM: LETTUCE_MOBILE,
  HERBS_FARM: HERBS_MOBILE,
  MUSHROOM_FARM: NAM_TUOI_NGON_MOBILE,
};

const Meta = Card.Meta;
export const ProductSkeleton = () => (
  <List gutter={[16, 16]}>
    {[...Array(5)].map((_, idx) => (
      <div key={idx}>
        <Card style={{ width: '100%' }} styles={{ cover: { width: '100%' } }} cover={<SkeletonImage active />}>
          <Meta title={<Skeleton.Input active style={{ height: '20px' }} />} />
          <Meta
            style={{ marginTop: '4px', width: '60%' }}
            title={<Skeleton.Input active style={{ height: '20px' }} />}
          />
          <Meta
            style={{ marginTop: '8px', width: '80%' }}
            title={<Skeleton.Input active style={{ height: '20px' }} />}
          />
          <Meta
            style={{ marginLeft: 'auto', marginTop: '10px', width: '75%' }}
            title={<Skeleton.Input active style={{ height: '30px' }} />}
          />
        </Card>
      </div>
    ))}
  </List>
);

const ProductsSource: FC<IProps> = (props) => {
  const [selectedProduct, setSelectedProduct] = useState<Product>();
  const [selectedSection, setSelectedSection] = useState<string>('');

  const [selectedIndex, setSelectedIndex] = useState<number>(-1);
  const [isOpenNoteModal, setIsOpenNoteModal] = useState<boolean>(false);
  const [showAllInSection, setShowAllInSection] = useState({});
  const lang = localStorage.getItem('lang') || 'vi';
  const {
    farmToTablePage: { sections },
    gotoSignIn,
    onToggleFavorite,
    onSetProductNote,
    addedProducts,
    addingProducts,
    permissions,
    noteItemLoading,
    gotoTagPage,
  } = props;
  const loggedIn = !!localStorage.getItem('token');

  const onClickOpenNoteModal = useCallback(
    (product: Product, index: number, sectionName: string) => {
      if (loggedIn) {
        setSelectedProduct(product);
        setSelectedSection(sectionName);
        setSelectedIndex(index);
        setIsOpenNoteModal(true);
      } else {
        gotoSignIn();
      }
    },
    [setSelectedProduct, setSelectedIndex, setIsOpenNoteModal, loggedIn],
  );

  const onClickCloseNoteModal = useCallback(() => {
    setSelectedProduct(undefined);
    setSelectedSection('');
    setSelectedIndex(-1);
    setIsOpenNoteModal(false);
  }, [setSelectedProduct, setSelectedIndex, setIsOpenNoteModal]);

  const scrollToSection = (id, offset = 100) => {
    const element = document.getElementById(id);
    if (element) {
      const top = element.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top: top });
    }
  };

  const onSubmitAddingNote = useCallback(
    (note: string) => {
      if (selectedProduct) {
        onSetProductNote({
          index: selectedIndex,
          section: selectedSection,
          note: note,
          id: selectedProduct.id,
        });
      }
      onClickCloseNoteModal();
    },
    [selectedProduct, selectedIndex, onClickCloseNoteModal, selectedSection],
  );
  return (
    <SectionContainer>
      <Container>
        <SectionTitleBox>
          {lang === 'vi' ? (
            <>
              <SectionTitle>Rau củ tươi mỗi ngày&nbsp;</SectionTitle>
              <SectionTitle highLight>từ nông trại hợp tác</SectionTitle>
            </>
          ) : (
            <>
              <SectionTitle>Products You Can Source </SectionTitle>
              <SectionTitle highLight>Through Our Farm Partners</SectionTitle>
            </>
          )}
        </SectionTitleBox>
        {Object.entries(sections).map(([sectionName, sectionData]) =>
          !!sectionData?.data?.length || sectionData?.loading ? (
            <Viewport.Consumer key={sectionName}>
              {({ width }) => (
                <ProductSourceContainer id={sectionName}>
                  <SectionTitleWrapper>
                    <SectionTitleBackground
                      src={width > 767 ? sectionImages[sectionName] : sectionImagesMobile[sectionName]}
                    />
                    <SectionTitleText>
                      <FormattedMessage {...messages[sectionName]} />
                    </SectionTitleText>
                  </SectionTitleWrapper>
                  <List>
                    {!!sectionData?.data?.length &&
                      (() => {
                        const data = showAllInSection[sectionName] ? sectionData?.data : sectionData?.data.slice(0, 5);
                        return data?.map((product: Product, index: number) => (
                          <Item key={product.id}>
                            <MarketItem
                              vertialOnly
                              listName="Section Product List"
                              loggedIn={loggedIn}
                              gotoSignIn={gotoSignIn}
                              product={product}
                              favorite={product.isFavorite}
                              note={product.note}
                              added={addedProducts[product.id]}
                              adding={addingProducts[product.id]}
                              position={index}
                              permissions={permissions}
                              onToggleFavorite={() =>
                                onToggleFavorite({ product: product, index: index, section: sectionName })
                              }
                              onOpenNote={() => onClickOpenNoteModal(product, index, sectionName)}
                              onDeleteNote={() => {
                                onSetProductNote({ index: index, section: sectionName, note: '', id: product.id });
                                onClickCloseNoteModal();
                              }}
                              impressionProperties={{ type: TrackingProductImpressionType.CartProduct }}
                              addToCartProperties={{ addFrom: TrackingAddToCartAddFromType.CartProduct }}
                              ga4Callback={({ category, supplierInternalCode, id }) =>
                                sendGA4Event(EventName.CLICK_PRODUCT_ITEM_FTB, {
                                  product_category_ftb: `${category?.parent?.name}/${category?.name}`,
                                  product_sku_ftb: supplierInternalCode,
                                  product_id_ftb: id,
                                })
                              }
                            />
                          </Item>
                        ));
                      })()}
                  </List>
                  {(sectionData?.loading || !sectionData?.data?.length) && <ProductSkeleton />}
                  <ModalNote
                    headerLabel={(selectedProduct && selectedProduct.name) || ''}
                    isOpen={isOpenNoteModal}
                    confirmLoading={noteItemLoading}
                    onSubmit={onSubmitAddingNote}
                    onClose={onClickCloseNoteModal}
                    note={(selectedProduct && selectedProduct.note) || ''}
                  />
                  {Number(sectionData?.totalResults) > 5 && (
                    <ButtonWrapper>
                      {width > 1024 ? (
                        <ButtonContainer
                          loading={sectionData?.loading}
                          onClick={() => {
                            setShowAllInSection({
                              ...showAllInSection,
                              [sectionName]: !showAllInSection?.[sectionName],
                            });
                            if (showAllInSection?.[sectionName]) {
                              scrollToSection(sectionName);
                            }
                          }}
                        >
                          {showAllInSection?.[sectionName] ? (
                            <FormattedMessage {...messages.collapse} />
                          ) : (
                            <FormattedMessage {...messages.seeMore} values={{ count: sectionData?.data?.length - 5 }} />
                          )}
                        </ButtonContainer>
                      ) : (
                        <ButtonContainer
                          loading={sectionData?.loading}
                          onClick={() => {
                            gotoTagPage(`/${sectionName}`);
                          }}
                        >
                          <FormattedMessage {...messages.seeAll} />
                        </ButtonContainer>
                      )}
                    </ButtonWrapper>
                  )}
                </ProductSourceContainer>
              )}
            </Viewport.Consumer>
          ) : (
            <></>
          ),
        )}
      </Container>
    </SectionContainer>
  );
};

const mapStateToProps = createStructuredSelector<ApplicationRootState, IStateProps>({
  farmToTablePage: selectFarmToTablePage(),
  addedProducts: selectAddedProducts(),
  addingProducts: selectAddingProducts(),
  permissions: selectUserPermissions(),
  noteItemLoading: selectNoteItemLoading(),
});
const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
  onToggleFavorite: (data: IHomeFavoritePayload) => dispatch(setProductSourceFavorite(data)),
  onSetProductNote: (data: IHomeProductNotePayload) => dispatch(setProductSourceNote(data)),
  gotoSignIn: () => dispatch(push('/signin')),
  gotoTagPage: (path) => dispatch(push(path)),
});

const withConnect = connect(mapStateToProps, mapDispatchToProps);

export default compose(withConnect)(ProductsSource);
